import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { ProductPricing } from '../types/models/ProductPricing';
import type { ApiResponseAdminProductPricing } from '../types/api/ApiResponseAdminProduct';

interface UseProductPricingReturn {
  pricing: ProductPricing | null;
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  /** Re-fetch and return the fresh pricing (or null on failure) so callers can read the new override count. */
  refetch: () => Promise<ProductPricing | null>;
}

// GET /admin/products/{id}/price — the platform default plus every per-client override for one
// product. Lazy: `enabled` (e.g. the overrides modal's isOpen) defers the fetch until it's needed,
// and `productId === null` is a no-op. Staff-only on the backend; useApiFetch injects the token.
export const useProductPricing = (productId: number | null, enabled = true): UseProductPricingReturn => {
  const [pricing, setPricing] = useState<ProductPricing | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchPricing = useCallback(async (): Promise<ProductPricing | null> => {
    if (productId === null) {
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/admin/products/${productId}/price`);
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseAdminProductPricing | null;
      if (body?.status === 'success' && body.data?.price) {
        setPricing(body.data.price);
        return body.data.price;
      }
      setError(body?.message ?? 'Failed to load product pricing');
      return null;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching pricing');
      return null;
    } finally {
      setLoading(false);
    }
  }, [apiFetch, productId]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated && productId !== null) {
      void fetchPricing();
    }
  }, [enabled, auth.isAuthenticated, productId, fetchPricing]);

  return { pricing, loading, error, refetch: fetchPricing };
};
